import React from "react";
import { Page } from "../../activities/AppPage";
import { PatientId, PatientTaskBroadcastDetails, PatientTaskInstanceDetails } from "../../messages";
import { AgencyMember } from "../../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { AgencyDetailsCard } from "../AgencyDetailsCard";
import FinishOnboardingBeforeStaffingModal from "../FinishOnboardingBeforeStaffingModal";
import PatientAvatar from "../PatientAvatar/PatientAvatar";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import AvailableTaskDetails from "./components/AvailableTaskDetails";
import AvailableTaskFooter from "./components/AvailableTaskFooter";
import AvailableTaskRequiredDocuments from "./components/AvailableTaskRequiredDocuments";
import OnboardingTaskDetails from "./components/OnboardingTaskDetails";

interface Props {
  caregiver: AppCaregiver;
  taskBroadcastDetails: PatientTaskBroadcastDetails | PatientTaskInstanceDetails;
  onClickLocation: () => void;
  onClickGoBack: () => void;
  onClickChat: (patientId: PatientId) => void;
  onClickCall: (phonenumber: string | null) => void;
  onClickApprove: () => void;
  onRequestNavigate: (page: Page) => void;
  agencyMember?: AgencyMember;
}

export const AvailableTaskScreen = (props: Props) => {
  const [onboardingModalOpen, setOnboardingModalOpen] = React.useState(false);

  const handleClickChat = () => {
    props.onClickChat(props.taskBroadcastDetails.patientId);
  };

  const handleClickCall = () => {
    props.onClickCall(props.taskBroadcastDetails.agency.officePhone);
  };

  const handleClickTakeThisTask = () => {
    if (props.taskBroadcastDetails.type === "Onboarding") {
      setOnboardingModalOpen(true);
    } else {
      props.onClickApprove();
    }
  };

  const handleClickLinkedPatient = () => {
    if (props.taskBroadcastDetails.linkedPatientTaskInstanceId !== null) {
      props.onRequestNavigate({
        type: "AvailableTaskScreen",
        taskBroadcastId: props.taskBroadcastDetails.linkedPatientTaskInstanceId,
      });
    }
  };

  const timeframe = {
    dayTimeParams: props.taskBroadcastDetails.dayTimeParams,
    dueDate: props.taskBroadcastDetails.dueDate,
    startDate: props.taskBroadcastDetails.startDate,
  };

  return (
    <Scaffold
      title={<ScaffoldTitle label={props.taskBroadcastDetails.title} />}
      endActions={
        <PatientAvatar
          key={props.taskBroadcastDetails.patientInitials}
          initials={props.taskBroadcastDetails.patientInitials}
        />
      }
      footer={
        <AvailableTaskFooter selectedDateTime={null} onClickApprove={handleClickTakeThisTask} />
      }
      onClickGoBack={props.onClickGoBack}
      className="AvailableTaskScreen"
    >
      {(() => {
        switch (props.taskBroadcastDetails.type) {
          case "Available":
            return (
              <AvailableTaskDetails
                {...props.taskBroadcastDetails}
                timeframe={timeframe}
                onClickLocation={props.onClickLocation}
                onClickLinkedPatient={handleClickLinkedPatient}
                onClickPatientSchedule={() =>
                  props.onRequestNavigate({
                    type: "PatientSchedule",
                    patientTaskInstanceId: props.taskBroadcastDetails.taskInstanceId,
                  })
                }
              />
            );

          case "Onboarding":
            return (
              <OnboardingTaskDetails
                {...props.taskBroadcastDetails}
                onClickLocation={props.onClickLocation}
                onClickPatientSchedule={() =>
                  props.onRequestNavigate({
                    type: "PatientSchedule",
                    patientTaskInstanceId: props.taskBroadcastDetails.taskInstanceId,
                  })
                }
              />
            );
        }
      })()}

      <AvailableTaskRequiredDocuments documents={props.taskBroadcastDetails.documents} />

      <AgencyDetailsCard
        agency={props.taskBroadcastDetails.agency}
        coordinatorInCharge={props.agencyMember}
        onClickChat={handleClickChat}
        onClickCall={handleClickCall}
      />

      <FinishOnboardingBeforeStaffingModal
        isOpen={onboardingModalOpen}
        onRequestClose={() => setOnboardingModalOpen(false)}
        onClickFinishOnboarding={() => {
          setOnboardingModalOpen(false);
          props.onRequestNavigate({ type: "OnboardingActivity" });
        }}
      />
    </Scaffold>
  );
};
